import * as CookieConsent from "vanilla-cookieconsent";
import GoogleTags from "./GoogleTags";
import Meta from "./Meta";

const loggedConsents = new Set();

let cookieConsentInitialized = false;

const logConsentToDatabase = async (
	consentId,
	consentGiven,
	consentPurpose,
	methodOfConsent,
	legalBasis = "consent"
) => {
	console.log("=== COOKIE CONSENT DEBUG ===");
	console.log("Function called with:", {
		consentId,
		consentGiven,
		consentPurpose,
		methodOfConsent,
		legalBasis
	});

	const logKey = `${consentId}-${consentPurpose}-${methodOfConsent}-${consentGiven}`;
	console.log("Log key:", logKey);

	const isPreferenceChange = methodOfConsent === "preferences_modal_change";
	console.log("Is preference change:", isPreferenceChange);

	if (!isPreferenceChange && loggedConsents.has(logKey)) {
		console.log("Already logged, skipping");
		return;
	}

	if (!isPreferenceChange) {
		loggedConsents.add(logKey);
		console.log("Added to logged consents set");
	}

	let thirdPartySharing = "none";
	if (consentPurpose === "meta_pixel") {
		thirdPartySharing = "meta";
	} else if (consentPurpose !== "necessary") {
		thirdPartySharing = "google";
	}

	const consentData = {
		consent_id: consentId,
		consent_given: consentGiven,
		consent_purpose: consentPurpose,
		legal_basis: legalBasis,
		method_of_consent: methodOfConsent,
		privacy_policy_version: "1.0",
		third_party_sharing: thirdPartySharing,
		retention_period: "2 years",
	};

	try {
		// Determine third party sharing based on consent purpose
		console.log("Sending consent data:", consentData);

		const response = await fetch("/php/routes/cookieConsent.php", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify(consentData),
		});

		console.log("Response status:", response.status);
		console.log("Response headers:", response.headers);

		if (!response.ok) {
			const errorText = await response.text();
			console.error("Server error response:", errorText);
			throw new Error(`HTTP error! status: ${response.status}, response: ${errorText}`);
		}

		const result = await response.json();
		console.log("Success response:", result);
	} catch (error) {
		console.error("Failed to log consent:", error);
		console.error("Request details:", {
			url: "/php/routes/cookieConsent.php",
			method: "POST",
			data: consentData,
			error: error.message
		});
		if (!isPreferenceChange) {
			loggedConsents.delete(logKey);
		}
	}
};

const cookies = () => {
	console.log("=== COOKIES FUNCTION CALLED ===");
	console.log("cookieConsentInitialized:", cookieConsentInitialized);

	if (cookieConsentInitialized) {
		console.log("Cookie consent already initialized, returning");
		return;
	}

	console.log("Initializing cookie consent...");
	cookieConsentInitialized = true;

	console.log("About to call CookieConsent.run()");
	CookieConsent.run({
		guiOptions: {
			consentModal: {
				layout: "bar",
				position: "bottom",
				equalWeightButtons: true,
				flipButtons: false,
			},
			preferencesModal: {
				layout: "box",
				position: "left",
				equalWeightButtons: true,
				flipButtons: false,
			},
		},
		disablePageInteraction: false,
		categories: {
			necessary: {
				enabled: true,
				readOnly: true,
			},
			analytics: {
				enabled: false,
			},
			ad_storage: {
				enabled: false,
			},
			ad_user_data: {
				enabled: false,
			},
			ad_personalization: {
				enabled: false,
			},
			meta_pixel: {
				enabled: false,
			},
		},
		language: {
			default: "en",
			translations: {
				en: {
					consentModal: {
						title: "We value your privacy",
						description:
							"Like most websites you'll visit, this one uses cookies to record information about traffic statistics and online interactions on the website. This helps us maintain and improve the quality of service we provide our customers.",
						acceptAllBtn: "Accept All Cookies",
						acceptNecessaryBtn: "Accept Essential Only",
						showPreferencesBtn: "Manage Cookie Preferences",
						footer:
							'<a href="/cookies/">Cookie Policy</a>\n<a href="/privacy/">Privacy Policy</a>',
					},
					preferencesModal: {
						title: "Cookie Preferences",
						acceptAllBtn: "Accept All Cookies",
						acceptNecessaryBtn: "Accept Essential Only",
						savePreferencesBtn: "Save Preferences",
						closeIconLabel: "Close modal",
						serviceCounterLabel: "Service|Services",
						sections: [
							{
								title: "Cookie Usage",
								description:
									'<p>In order to make this website function properly, we use cookies and similar software. Some are necessary and these will run anyway to make your website viewing experience as smooth as possible. Those related to traffic data recording are optional, and you can manage your preferences here or at any other time through the footer of our website.</p><p>You can find out more in our <a href="/privacy/" title="Read privacy notice">Privacy Notice</a> and <a href="/cookies/" title="Read more about cookies">Cookie Policy</a>.</p><p>If you are happy to accept cookies and continue to the website click \'Accept all\' and carry on.</p>',
							},
							{
								title:
									'Strictly Necessary Cookies <span class="pm__badge">Always Active</span>',
								description:
									"These cookies are essential for the proper functioning of the website and cannot be disabled. They enable basic features such as page navigation and access to secure areas.",
								linkedCategory: "necessary",
							},
							{
								title: "Google Analytics",
								description:
									"We use Google Analytics to keep track of how many users are accessing our website, and to see which pages they browse through. Analytics helps us to understand our audience so that we can deliver a better service to everyone.",
								linkedCategory: "analytics",
							},
							{
								title: "Google Ads - Storage",
								description:
									"Cookie for Google Ads to track conversions and serve targeted advertisements.",
								linkedCategory: "ad_storage",
							},
							{
								title: "Google Ads - User Data",
								description:
									"These cookies collect data on your interactions with Google Ads, such as ad clicks or completed actions.",
								linkedCategory: "ad_user_data",
							},
							{
								title: "Google Ads - Personalisation",
								description:
									"These cookies personalise the ads you see based on your behaviour and interests.",
								linkedCategory: "ad_personalization",
							},
							{
								title: "Meta Pixel (Facebook)",
								description:
									"These cookies are used by Meta (Facebook/Instagram) to deliver and measure advertising, track conversions, and provide analytics data. They help us understand how users interact with our ads on Meta platforms.",
								linkedCategory: "meta_pixel",
							},
						],
					},
				},
			},
		},
		onConsent: ({ cookie }) => {
			console.log("=== onConsent FIRED ===");
			console.log("Cookie object:", cookie);
			console.log("Cookie categories:", cookie.categories);
			console.log("Cookie consentId:", cookie.consentId);

			// Initialize all consent as denied first
			GoogleTags.denyConsent("analytics_storage");
			GoogleTags.denyConsent("ad_storage");
			GoogleTags.denyConsent("ad_user_data");
			GoogleTags.denyConsent("ad_personalization");
			Meta.revokeConsent();

			// Log necessary cookies (always granted)
			console.log("About to log necessary consent...");
			logConsentToDatabase(
				cookie.consentId,
				true,
				"necessary",
				"cookie_banner_initial",
				"legitimate_interest"
			);

			// Handle analytics consent
			const analyticsAccepted = cookie.categories.includes("analytics");
			if (analyticsAccepted) {
				GoogleTags.grantConsent("analytics_storage");
			}
			logConsentToDatabase(
				cookie.consentId,
				analyticsAccepted,
				"analytics_storage",
				"cookie_banner_initial"
			);

			// Handle ad storage consent
			const adStorageAccepted = cookie.categories.includes("ad_storage");
			if (adStorageAccepted) {
				GoogleTags.grantConsent("ad_storage");
			}
			logConsentToDatabase(
				cookie.consentId,
				adStorageAccepted,
				"ad_storage",
				"cookie_banner_initial"
			);

			// Handle ad user data consent
			const adUserDataAccepted = cookie.categories.includes("ad_user_data");
			if (adUserDataAccepted) {
				GoogleTags.grantConsent("ad_user_data");
			}
			logConsentToDatabase(
				cookie.consentId,
				adUserDataAccepted,
				"ad_user_data",
				"cookie_banner_initial"
			);

			// Handle ad personalization consent
			const adPersonalizationAccepted = cookie.categories.includes("ad_personalization");
			if (adPersonalizationAccepted) {
				GoogleTags.grantConsent("ad_personalization");
			}
			logConsentToDatabase(
				cookie.consentId,
				adPersonalizationAccepted,
				"ad_personalization",
				"cookie_banner_initial"
			);

			// Handle Meta pixel consent
			const metaPixelAccepted = cookie.categories.includes("meta_pixel");
			if (metaPixelAccepted) {
				Meta.grantConsent();
			} else {
				Meta.revokeConsent();
			}
			logConsentToDatabase(
				cookie.consentId,
				metaPixelAccepted,
				"meta_pixel",
				"cookie_banner_initial"
			);

			// Set prompt as seen in GoogleTags
			GoogleTags.setPromptSeen();
		},
		onChange: ({ cookie, changedCategories }) => {
			// Handle analytics preference changes
			const analyticsAccepted = cookie.categories.includes("analytics");
			if (analyticsAccepted) {
				GoogleTags.grantConsent("analytics_storage");
			} else {
				GoogleTags.denyConsent("analytics_storage");
			}
			logConsentToDatabase(
				cookie.consentId,
				analyticsAccepted,
				"analytics_storage",
				"preferences_modal_change"
			);

			// Handle ad storage preference changes
			const adStorageAccepted = cookie.categories.includes("ad_storage");
			if (adStorageAccepted) {
				GoogleTags.grantConsent("ad_storage");
			} else {
				GoogleTags.denyConsent("ad_storage");
			}
			logConsentToDatabase(
				cookie.consentId,
				adStorageAccepted,
				"ad_storage",
				"preferences_modal_change"
			);

			// Handle ad user data preference changes
			const adUserDataAccepted = cookie.categories.includes("ad_user_data");
			if (adUserDataAccepted) {
				GoogleTags.grantConsent("ad_user_data");
			} else {
				GoogleTags.denyConsent("ad_user_data");
			}
			logConsentToDatabase(
				cookie.consentId,
				adUserDataAccepted,
				"ad_user_data",
				"preferences_modal_change"
			);

			// Handle ad personalization preference changes
			const adPersonalizationAccepted = cookie.categories.includes("ad_personalization");
			if (adPersonalizationAccepted) {
				GoogleTags.grantConsent("ad_personalization");
			} else {
				GoogleTags.denyConsent("ad_personalization");
			}
			logConsentToDatabase(
				cookie.consentId,
				adPersonalizationAccepted,
				"ad_personalization",
				"preferences_modal_change"
			);

			// Handle Meta pixel preference changes
			const metaPixelAccepted = cookie.categories.includes("meta_pixel");
			if (metaPixelAccepted) {
				Meta.grantConsent();
			} else {
				Meta.revokeConsent();
			}
			logConsentToDatabase(
				cookie.consentId,
				metaPixelAccepted,
				"meta_pixel",
				"preferences_modal_change"
			);
		},
	});
};

export default cookies;
